import React from 'react';
import * as Yup from 'yup';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { Form } from '@dashboard/components/Formik';
import Link from '@dashboard/components/Link';
import PageFilterInput from '@dashboard/components/Form/components/PageFilterInput';
import getSellerDomain from '@/dashboard/services/getSellerDomain';
import hasLinkInBioLab from '@dashboard/lib/linkInBioLab';

const validationSchema = Yup.object().shape({
  homepage: Yup.object().shape({
    type: Yup.string().oneOf(['customerPortal', 'checkout', 'redirect', 'linkInBio', '']),
    checkout: Yup.string().when('type', {
      is: 'checkout',
      then: Yup.string().required('Checkout is required'),
    }),
    redirectUrl: Yup.string().when('type', {
      is: 'redirect',
      then: Yup.string().url().required('Redirect URL is required'),
    }),
  }),
});

const HomepageSettingsForm = () => {
  const { seller } = useAuth();

  const { updateSeller } = useSellerQueries();

  return (
    <Form
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={{
        homepage: seller.homepage,
      }}
      fields={({ values }) => [
        {
          name: 'homepage.type',
          required: false,
          type: 'select',
          label: 'Homepage',
          labelDescription: 'Display your customer portal, a page, or redirect to a URL.',
          options: [
            { value: 'customerPortal', label: 'Customer portal' },
            { value: 'checkout', label: 'Page' },
            ...(hasLinkInBioLab(seller) || seller.homepage?.type === 'linkInBio'
              ? [{ value: 'linkInBio', label: 'Link in bio' }]
              : []),
            { value: 'redirect', label: 'Redirect to URL' },
            { value: '', label: 'No homepage (blank)' },
          ],
          additionalContent: (
            <div className="mt-2 text-sm">
              <Link href={getSellerDomain(seller)} target="_blank" external>
                View homepage
              </Link>
              {values.homepage?.type === 'linkInBio' && (
                <>
                  {' · '}
                  <Link to="/link-in-bio">Edit link in bio</Link>
                </>
              )}
            </div>
          ),
        },

        ...(values.homepage?.type === 'checkout'
          ? [
              {
                name: 'homepage.checkout',
                required: true,
                type: 'custom',
                label: 'Page',
                renderField: (field, { values, setFieldValue }) => (
                  <PageFilterInput
                    name={field.name}
                    placeholder="Select a page"
                    pageTypes={['checkout', 'event', 'form']}
                    initialValue={values.homepage?.checkout}
                    onChange={value => setFieldValue(field.name, value)}
                  />
                ),
              },
            ]
          : []),
        ...(values.homepage?.type === 'redirect'
          ? [
              {
                name: 'homepage.redirectUrl',
                required: true,
                type: 'url',
                label: 'Redirect URL',
              },
            ]
          : []),
      ]}
      onSubmit={async values => {
        try {
          const { data } = await updateSeller(values);

          return {
            data,
          };
        } catch (error) {
          return {
            error,
          };
        }
      }}
    />
  );
};

export default HomepageSettingsForm;
